import Joi from 'joi';

import { AppSubjectEnum } from '../../../../shared/modules/authz/enums/app-subject.enum';
import { pricingItemSchema } from '../../../../shared/modules/pricing-items/validators/schemas/pricing-item.schema';
import { agreementContactSchema } from '../../../contacts/validators/schemas/agreement-contact.schema';
import { createPoliciesAssignSchema } from '../../../policies/validators/schemas/policies-assign.schema';
import { createTagsSchema } from '../../../tags/validators/schemas/create-tags.schema';
import { RentalAgreementRecordCategoryEnum } from '../../enums/rental-agreement-record-category.enum';
import { RentalAgreementTypeEnum } from '../../enums/rental-agreement-type.enum';
import { RentalPartySourceTypeEnum } from '../../enums/rental-party-source-type.enum';

/**
 * Schema for new rental agreement record validation.
 */
export const createRentalAgreementSchema = () =>
  Joi.object({
    recordCategory: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid':
          'validations|rental-agreement-record-category|cannot-be-null',
      }),
      otherwise: Joi.string()
        .valid(...Object.values(RentalAgreementRecordCategoryEnum))
        .required()
        .messages({
          'any.required':
            'validations|rental-agreement-record-category|required',
          'any.only':
            'validations|rental-agreement-record-category|invalid-value',
          'string.base':
            'validations|rental-agreement-record-category|must-be-string',
          'string.empty':
            'validations|rental-agreement-record-category|cannot-be-empty',
        }),
    }),

    type: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid': 'validations|rental-agreement-type|cannot-be-null',
      }),
      otherwise: Joi.string()
        .valid(...Object.values(RentalAgreementTypeEnum))
        .required()
        .messages({
          'any.required': 'validations|rental-agreement-type|required',
          'any.only': 'validations|rental-agreement-type|invalid-value',
          'string.base': 'validations|rental-agreement-type|must-be-string',
          'string.empty': 'validations|rental-agreement-type|cannot-be-empty',
        }),
    }),

    startsAt: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid': 'validations|rental-agreement-starts-at|cannot-be-null',
      }),
      otherwise: Joi.date().required().messages({
        'any.required': 'validations|rental-agreement-starts-at|required',
        'date.base':
          'validations|rental-agreement-starts-at|must-be-a-valid-date',
      }),
    }),

    endsAt: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid': 'validations|rental-agreement-ends-at|cannot-be-null',
      }),
      otherwise: Joi.date().greater(Joi.ref('startsAt')).required().messages({
        'any.required': 'validations|rental-agreement-ends-at|required',
        'date.base':
          'validations|rental-agreement-ends-at|must-be-a-valid-date',
        'date.greater':
          'validations|rental-agreement-ends-at|cannot-be-before-starts-at',
      }),
    }),

    vehicleId: Joi.when('recordCategory', {
      is: [RentalAgreementRecordCategoryEnum.VEHICLE],
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|rental-agreement-vehicle-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required': 'validations|rental-agreement-vehicle-id|required',
          'number.base':
            'validations|rental-agreement-vehicle-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    vehicleCategoryId: Joi.when('recordCategory', {
      is: [RentalAgreementRecordCategoryEnum.VEHICLE_CATEGORY],
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|rental-agreement-vehicle-category-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required': 'validations|rental-agreement-vehicle-id|required',
          'number.base':
            'validations|rental-agreement-vehicle-category-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    pickupLocationId: Joi.number().allow(null).messages({
      'number.base':
        'validations|rental-agreement-pickup-location-id|must-be-number',
    }),

    dropOffLocationId: Joi.number().allow(null).messages({
      'number.base':
        'validations|rental-agreement-dropoff-location-id|must-be-number',
    }),

    partyType: Joi.string()
      .valid(...Object.values(RentalPartySourceTypeEnum))
      .required()
      .messages({
        'any.required': 'validations|rental-agreement-party-type|required',
        'any.only': 'validations|rental-agreement-party-type|invalid-value',
      }),

    customerId: Joi.when('partyType', {
      is: RentalPartySourceTypeEnum.CUSTOMER,
      then: Joi.number().integer().positive().required().messages({
        'any.required': 'validations|rental-agreement-customer-id|required',
        'number.base': 'validations|rental-agreement-customer-id|must-be-number',
      }),
      otherwise: Joi.valid(null).messages({
        'any.only': 'validations|rental-agreement-customer-id|not-allowed',
      }),
    }),

    accountUuid: Joi.when('partyType', {
      is: RentalPartySourceTypeEnum.ACCOUNT,
      then: Joi.string().guid({ version: 'uuidv4' }).required().messages({
        'any.required': 'validations|rental-agreement-account-uuid|required',
        'string.guid': 'validations|rental-agreement-account-uuid|must-be-uuid',
      }),
      otherwise: Joi.valid(null).messages({
        'any.only': 'validations|rental-agreement-account-uuid|not-allowed',
      }),
    }),

    agentId: Joi.when('type', {
      is: RentalAgreementTypeEnum.AGENT,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|rental-agreement-agent-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required': 'validations|rental-agreement-agent-id|required',
          'number.base': 'validations|rental-agreement-agent-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    color: Joi.string().optional().allow(null),

    notes: Joi.string().optional().allow(null, ''),

    hasFullInsurance: Joi.boolean().optional().allow(null),

    agreementContact: agreementContactSchema().optional().allow(null),

    driverCustomerIds: Joi.array()
      .items(Joi.number().integer().positive())
      .unique()
      .optional()
      .allow(null),

    driverAccountUuids: Joi.array()
      .items(Joi.string().guid({ version: 'uuidv4' }))
      .unique()
      .optional()
      .allow(null),

    pricingItems: Joi.array().items(pricingItemSchema()).allow(null),

    tags: createTagsSchema().messages({
      'array.base': 'validations|rental-agreement-tags|invalid-value',
    }),

    policies: createPoliciesAssignSchema(AppSubjectEnum.RENTAL_AGREEMENT),

    files: Joi.any().optional(),
  })
    // Ignore extra fields
    .unknown();
